import React from 'react';
import { useNavigate } from '@tanstack/react-router';

import StepperCard from '@/components/common/StepperCard.tsx';
import { InsuranceCard } from '@/components/InsuranceCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { removeInsurance } from '@/types/insurance-builder-legacy.ts';
import {
  getActionableInsurances,
  getActionablePlanFamilyCarrierTypes,
} from '@/types/insurance-builder.type.ts';
import { type UnverifiedInsurance, type VerifiedInsurance } from '@/types/insurance.type.ts';

export function InsuranceFinalize() {
  const navigate = useNavigate();

  const {
    insuranceBuilder,
    patientRecipient,
    patientFirstName,
    patientLastName,
    patientDateOfBirth,
    patientGender,
  } = useSessionStore();

  const displayedInsurances = getActionableInsurances(insuranceBuilder);

  const patient = {
    recipient: patientRecipient as 'me' | 'someone-else',
    firstName: patientFirstName,
    lastName: patientLastName,
    dateOfBirth: patientDateOfBirth,
    gender: patientGender as 'male' | 'female',
  };

  const remove = (insurance: UnverifiedInsurance | VerifiedInsurance) => {
    const updatedBuilder = removeInsurance(insuranceBuilder, insurance);
    useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });

    // After removing an insurance, check if there are carrier types the user claimed
    // to have but no longer have actionable insurance for
    const actionableTypes = getActionablePlanFamilyCarrierTypes(updatedBuilder);
    const missingCarrierTypes = updatedBuilder.carrierTypes.filter((t) => !actionableTypes.has(t));

    if (missingCarrierTypes.length > 0) navigate({ to: `/insurance/options` });
  };

  const toInsuranceStart = () => navigate({ to: '/insurance/pre-discovery' });
  const toBenefits = () => navigate({ to: '/insurance/benefits' });
  const toAddInsurance = () => navigate({ to: '/insurance/input' });

  return (
    <section>
      <NavigationBar onBack={toInsuranceStart} backLabel="Insurance" stepIndicator="4 of 4" />
      <StepperCard currentStep={4} totalSteps={4}>
        <CardHeader>
          <CardTitle>
            {insuranceBuilder.unverifiedInsurances.length > 0 ? (
              <>Thanks—We’ll confirm your coverage and reach out if needed</>
            ) : (
              <>Please confirm your insurance details</>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-8 sm:space-y-12">
          <div className="flex flex-wrap justify-center gap-4.5 sm:justify-start sm:gap-8">
            {displayedInsurances.map((insurance, index) => (
              <div key={index} className="w-full max-w-80">
                <InsuranceCard
                  insurance={insurance}
                  policyHolder={patient}
                  onRemove={() => remove(insurance)}
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4.5">
            <Button
              className="w-full justify-self-center sm:w-auto sm:justify-self-start"
              onClick={toBenefits}
            >
              Looks correct
            </Button>

            <p className="flex flex-wrap items-center justify-center justify-self-center sm:justify-self-start">
              <span>Something not right?</span>
              <Button className="pl-2 sm:pl-2" onClick={toAddInsurance} size="sm" variant="link">
                Add your insurance ID
              </Button>
            </p>
          </div>
        </CardContent>
      </StepperCard>
    </section>
  );
}
